/**
 * Üst çubuk genel arama (WP 2.11, K-13…K-19; K-2.11-A): Ctrl/⌘+K veya düğme ile açılır; gerçek `POST /search/query` ucuna
 * bağlıdır (sahte sonuç yok). Erişilebilirlik: WAI-ARIA combobox + listbox (bölümler `group`), `aria-activedescendant` ile ok
 * tuşu gezinmesi, Enter seçer, Escape kapatır ve odak tetikleyiciye döner (Dialog), durum `aria-live` ile duyurulur.
 * İstek: 250 ms gecikme; anahtar değişince önceki istek iptal edilir, eski yanıt yeni sorguyu ezemez (TanStack anahtarı).
 * K-2.11-A: geniş sorguda bölüm sınırlı bir aday penceresidir. Tarama bitmediyse (`hasMore`) boş bölüm "sonuç yok" diye
 * sunulmaz; "Aramaya devam et" seçeneği o bölümün sonraki penceresini TEK istekle getirir ve sonuçlar yinelenmeden eklenir
 * (otomatik sınırsız tarama yok). "Sonuç bulunamadı" yalnız arama gerçekten tamamlandığında gösterilir.
 * Sorgu metni yalnız istek gövdesinde ve tam sonuç sayfasının URL parçasında (`#q=`) taşınır; son aramalar saklanmaz.
 * v3.9 "Sayfaya git": kayıt sonuçlarının üstünde, kabuğun YETKİ süzgecinden geçmiş menüsünden kurulan sayfa eşleşmeleri
 * (`useNavPages`) gösterilir; istek atılmaz, yetkisi olmayan rota listede hiç yoktur. Etiket aramasında (#) sayfa önerilmez.
 * TI-013: tek arama yüzeyi — arama kutusu boşken hızlı erişim (kabukta yıldızla sabitlenen sayfalar; MEVCUT tarayıcı saklaması,
 * hesap eşitlemesi yok); kayıt sonucunda tür rozeti; geniş ekranda seçili sonucun hızlı önizlemesi (ok tuşu/üzerine gelme),
 * dar ekranda "Önizle" ile odak önizlemesi ve "Sonuçlara dön". Son aramalar / son kayıtlar SAKLANMAZ (açık karar 8).
 */
import {
  Suspense,
  lazy,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Eye } from 'lucide-react';
import { Button, Dialog, Spinner, cn, useMediaQuery } from '@castinflow/ui';
import type { SearchHit, SearchRecordType, SearchSection } from '@castinflow/shared';
import {
  searchErrorKind,
  searchRecords,
  useSearch,
  validateQuery,
  type SearchErrorKind,
} from './search-client';
import { searchStrings as s } from './search-strings';
import { SearchHitView } from './SearchHitView';
import { searchPagePath } from './search-route';
import { recordHref } from '../routes';
import { matchPages, useNavPages, type NavPage } from '../../shell/nav-context';
import { pinsKey, readPins } from '../../shell/preferences';
import { useScope } from '../../lib/scope';
import { navText } from '../../shell/navigation-strings';

// Önizleme tembel yüklenir: kayıt görünümü ve çerçeve metinleri kabuğun ana paketine girmez (scripts/check-web-bundle.mjs)
const SearchPreview = lazy(() =>
  import('./SearchPreview').then((m) => ({ default: m.SearchPreview })),
);

const DEBOUNCE_MS = 250;
const QUICK_LIMIT = 5;
const PAGE_LIMIT = 5;

function useDebounced(value: string, ms: number): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => {
      setV(value);
    }, ms);
    return () => {
      window.clearTimeout(t);
    };
  }, [value, ms]);
  return v;
}

type Option =
  | { kind: 'page'; page: NavPage; id: string }
  | { kind: 'hit'; hit: SearchHit; id: string }
  | { kind: 'more'; type: SearchRecordType; id: string }
  | { kind: 'all'; id: string };

/** Bölüm devamı: kullanıcı "Aramaya devam et" dedikçe eklenen sonuçlar + güncel imleç/durum */
interface Continuation {
  items: SearchHit[];
  cursor: string | null;
  partial: boolean;
  hasMore: boolean;
  loading: boolean;
  error: SearchErrorKind | null;
}

interface SectionView {
  sec: SearchSection;
  items: SearchHit[];
  cursor: string | null;
  partial: boolean;
  hasMore: boolean;
  loading: boolean;
  error: SearchErrorKind | null;
}

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    function onKey(e: globalThis.KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);
  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
        }}
        // Tema uyarlaması: üst barda hap biçimli arama (beyaz yüzey, zeminle bütünleşik bar); dar ekranda yuvarlak simge düğmesi
        className="flex h-control w-control items-center justify-center gap-2 rounded-pill border border-transparent bg-surface text-base text-fg-subtle shadow-card transition-[color,border-color] duration-fast hover:border-border hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus lg:w-80 lg:justify-start lg:px-4"
        aria-keyshortcuts="Control+K Meta+K"
        data-testid="global-search"
      >
        <Search size={16} aria-hidden="true" />
        <span className="hidden truncate lg:inline">{s.trigger}</span>
        <span className="sr-only lg:hidden">{s.trigger}</span>
        <kbd className="ml-auto hidden rounded-sm border border-border bg-surface-subtle px-1.5 text-xs lg:inline">
          {s.shortcut}
        </kbd>
      </button>
      {open ? (
        <SearchDialog
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

function SearchDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const base = useId().replace(/:/g, '');
  const [q, setQ] = useState('');
  const debounced = useDebounced(q.trim(), DEBOUNCE_MS);
  const valid = debounced !== '' && validateQuery(debounced).ok;
  const query = useSearch(valid ? { q: debounced, limit: QUICK_LIMIT } : null);
  const [active, setActive] = useState(-1);
  const [extra, setExtra] = useState<Partial<Record<SearchRecordType, Continuation>>>({});
  const abortRef = useRef<AbortController | null>(null);
  const navPages = useNavPages();
  const scope = useScope();
  const wide = useMediaQuery('(min-width: 768px)', true);
  const [previewHit, setPreviewHit] = useState<SearchHit | null>(null);
  // Hızlı erişim: kabukta sabitlenen sayfalar (yetki süzgecinden geçmiş menüden; yetkisi kalkan sayfa görünmez)
  const pinned = useMemo(() => {
    const ids = readPins(scope === null ? null : pinsKey(scope.tenantId, scope.userId));
    return ids
      .map((id) => navPages.find((p) => p.id === id))
      .filter((p): p is NavPage => p !== undefined);
  }, [navPages, scope]);
  const typed = q.trim();
  const pageHits = useMemo(
    () =>
      typed.length < 2 || typed.startsWith('#') ? [] : matchPages(navPages, typed, PAGE_LIMIT),
    [navPages, typed],
  );

  const data = valid ? query.data : undefined;
  // Yeni yanıt (yeni sorgu) → devamlar sıfırlanır, bekleyen devam isteği iptal edilir
  useEffect(() => {
    abortRef.current?.abort();
    setExtra({});
    setActive(-1);
  }, [data]);
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const sections = useMemo<SectionView[]>(
    () =>
      (data?.sections ?? []).map((sec) => {
        const e = extra[sec.type];
        if (e === undefined)
          return {
            sec,
            items: sec.items,
            cursor: sec.nextCursor,
            partial: sec.partial,
            hasMore: sec.hasMore,
            loading: false,
            error: null,
          };
        const seen = new Set(sec.items.map((h) => h.id));
        return {
          sec,
          items: [...sec.items, ...e.items.filter((h) => !seen.has(h.id))],
          cursor: e.cursor,
          partial: e.partial,
          hasMore: e.hasMore,
          loading: e.loading,
          error: e.error,
        };
      }),
    [data, extra],
  );

  const options = useMemo<Option[]>(() => {
    if (typed === '')
      return pinned.map((page) => ({ kind: 'page', page, id: `${base}-o-pin-${page.id}` }));
    const out: Option[] = pageHits.map((page) => ({
      kind: 'page',
      page,
      id: `${base}-o-page-${page.id}`,
    }));
    if (data === undefined) return out;
    for (const v of sections) {
      for (const hit of v.items) out.push({ kind: 'hit', hit, id: `${base}-o-${hit.id}` });
      if (v.hasMore && v.cursor !== null)
        out.push({ kind: 'more', type: v.sec.type, id: `${base}-o-more-${v.sec.type}` });
    }
    out.push({ kind: 'all', id: `${base}-o-all` });
    return out;
  }, [pageHits, data, sections, base, typed, pinned]);
  const activeOpt = options[active];
  // Geniş ekranda önizleme SEÇİLİ sonucu izler (ok tuşu/üzerine gelme; seçmeden istek atılmaz); dar ekranda "Önizle" ile açılır
  const shownHit: SearchHit | null =
    previewHit ?? (activeOpt?.kind === 'hit' ? activeOpt.hit : null);

  /** Bölümün sonraki aday penceresi: TEK istek (sunucu en çok küçük, belgelenmiş sayıda pencere ilerletir) */
  const continueSection = async (type: SearchRecordType) => {
    const v = sections.find((x) => x.sec.type === type);
    const cursor = v?.cursor ?? null;
    if (v === undefined || cursor === null || v.loading) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setExtra((p) => ({
      ...p,
      [type]: {
        items: p[type]?.items ?? [],
        cursor,
        partial: v.partial,
        hasMore: v.hasMore,
        loading: true,
        error: null,
      },
    }));
    try {
      const r = await searchRecords(
        { q: debounced, types: [type], limit: QUICK_LIMIT, cursor },
        ac.signal,
      );
      const next = r.sections.find((x) => x.type === type);
      setExtra((p) => ({
        ...p,
        [type]: {
          items: [...(p[type]?.items ?? []), ...(next?.items ?? [])],
          cursor: next?.nextCursor ?? null,
          partial: next?.partial ?? false,
          hasMore: next?.hasMore ?? false,
          loading: false,
          error: null,
        },
      }));
    } catch (e) {
      if (ac.signal.aborted) return;
      setExtra((p) => ({
        ...p,
        [type]: {
          items: p[type]?.items ?? [],
          cursor,
          partial: v.partial,
          hasMore: v.hasMore,
          loading: false,
          error: searchErrorKind(e),
        },
      }));
    }
  };

  const go = (href: string) => {
    onClose();
    navigate(href);
  };
  const choose = (o: Option | undefined) => {
    if (o === undefined) {
      if (valid) go(searchPagePath({ q: debounced }));
      return;
    }
    if (o.kind === 'more') {
      void continueSection(o.type);
      return;
    }
    if (o.kind === 'page') {
      go(o.page.href);
      return;
    }
    // kayıt bağlantısı istemcinin kendi rota tanımından (sunucu `href`'i ile aynı biçim) kurulur
    go(o.kind === 'hit' ? recordHref(o.hit.type, o.hit.id) : searchPagePath({ q: debounced }));
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (options.length > 0) setActive((i) => (i + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (options.length > 0) setActive((i) => (i <= 0 ? options.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(options[active]);
    } else if (
      e.key === 'ArrowRight' &&
      !wide &&
      activeOpt?.kind === 'hit' &&
      e.currentTarget.selectionStart === e.currentTarget.value.length
    ) {
      // Dar ekranda yan önizleme paneli yok: seçili sonucun önizlemesi sağ ok tuşuyla açılır (imleç metnin sonundayken;
      // metin içinde gezinmeyi bozmaz)
      e.preventDefault();
      setPreviewHit(activeOpt.hit);
    }
  };

  const listId = `${base}-list`;
  const hintId = `${base}-hint`;
  const pending = q.trim() !== debounced || (valid && query.isFetching);
  const errorKind = valid && query.isError ? searchErrorKind(query.error) : null;
  const total = sections.reduce((n, v) => n + v.items.length, 0);
  const incomplete = sections.some((v) => v.hasMore && v.cursor !== null);
  const continuing = sections.some((v) => v.loading);
  const contError = sections.find((v) => v.error !== null)?.error ?? null;
  const localInvalid = q.trim() !== '' && !validateQuery(q.trim()).ok;

  let status: string;
  if (q.trim() === '' || localInvalid) status = s.hint;
  else if (pending || continuing) status = s.loading;
  else if (errorKind !== null) status = s.error[errorKind];
  else if (contError !== null) status = s.error[contError];
  else if (data === undefined) status = '';
  else if (total === 0) status = incomplete ? s.partialEmpty : s.results(0);
  else status = incomplete ? s.resultsPartial(total) : s.results(total);
  if (pageHits.length > 0 && !localInvalid)
    status = [navText.pagesFound(pageHits.length), status].filter((x) => x !== '').join(' · ');
  const allIdx = options.findIndex((o) => o.kind === 'all');

  const optionClass = (idx: number) =>
    cn(
      'cursor-pointer rounded px-2 py-1.5',
      idx === active ? 'bg-selected ring-1 ring-focus' : 'hover:bg-surface-subtle',
    );

  return (
    <Dialog
      open
      onClose={onClose}
      title={s.dialogTitle}
      size="lg"
      initialFocusRef={inputRef}
      testId="search-dialog"
    >
      <div className="space-y-3">
        <label htmlFor={`${base}-input`} className="sr-only">
          {s.inputLabel}
        </label>
        <input
          ref={inputRef}
          id={`${base}-input`}
          type="search"
          role="combobox"
          aria-expanded={options.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-describedby={wide ? hintId : `${hintId} ${base}-preview-key`}
          aria-activedescendant={active >= 0 ? options[active]?.id : undefined}
          autoComplete="off"
          spellCheck={false}
          maxLength={400}
          value={q}
          placeholder={s.placeholder}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(-1);
            setPreviewHit(null);
          }}
          onKeyDown={onKeyDown}
          data-testid="search-input"
          className="block h-control w-full rounded border border-border-strong bg-surface px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        />
        {!wide ? (
          <span id={`${base}-preview-key`} className="sr-only">
            {s.preview.keyHint}
          </span>
        ) : null}
        <div
          id={hintId}
          role="status"
          aria-live="polite"
          className="flex min-h-5 items-center gap-2 text-sm text-fg-muted"
          data-testid="search-status"
        >
          {(pending || continuing) && !localInvalid && q.trim() !== '' ? (
            <Spinner size="sm" label={s.loading} />
          ) : null}
          <span>{status}</span>
        </div>
        {errorKind !== null ? (
          <div data-testid="search-error" data-kind={errorKind} className="flex items-center gap-2">
            {errorKind === 'other' ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  void query.refetch();
                }}
              >
                {s.retry}
              </Button>
            ) : null}
          </div>
        ) : null}
        {data?.index.state === 'building' ? (
          <p className="text-sm text-fg-muted" data-testid="search-building">
            {s.buildingNote}
          </p>
        ) : null}
        {data?.query.kind === 'tag' ? (
          <p className="text-sm text-fg-muted" data-testid="search-tag-matches">
            {data.query.tags.length === 0
              ? s.noTagMatch
              : s.tagMatches(data.query.tags.map((t) => t.name).join(', '))}
          </p>
        ) : null}
        {data !== undefined &&
        total === 0 &&
        errorKind === null &&
        !incomplete &&
        pageHits.length === 0 ? (
          <p className="text-sm text-fg-muted" data-testid="search-empty">
            {s.empty}
            {data.index.pending ? ` ${s.pendingNote}` : ''}
          </p>
        ) : null}
        {!wide && previewHit !== null ? (
          <Suspense fallback={<Spinner />}>
            <SearchPreview
              hit={previewHit}
              onOpen={() => {
                go(recordHref(previewHit.type, previewHit.id));
              }}
              onBack={() => {
                setPreviewHit(null);
              }}
            />
          </Suspense>
        ) : null}
        <div
          className={cn(
            'gap-4 md:grid md:grid-cols-[minmax(0,1fr)_15rem]',
            !wide && previewHit !== null && 'hidden',
            typed === '' && 'md:grid-cols-1',
          )}
        >
          <div
            id={listId}
            role="listbox"
            aria-label={s.dialogTitle}
            className={cn(
              'max-h-[60vh] space-y-3 overflow-y-auto',
              options.length === 0 && 'hidden',
            )}
          >
            {typed === '' && pinned.length > 0 ? (
              <div
                role="group"
                aria-labelledby={`${base}-g-pinned`}
                data-testid="search-group-pinned"
              >
                <div
                  id={`${base}-g-pinned`}
                  className="flex items-baseline justify-between px-2 pb-1 text-xs font-semibold uppercase text-fg-subtle"
                >
                  <span>{s.pinned.title}</span>
                </div>
                {pinned.map((page, idx) => (
                  <div
                    key={page.id}
                    id={options[idx]?.id}
                    role="option"
                    aria-selected={idx === active}
                    tabIndex={-1}
                    data-testid="search-pinned-option"
                    data-href={page.href}
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onClick={() => {
                      choose(options[idx]);
                    }}
                    className={cn(optionClass(idx), 'flex items-center gap-2.5')}
                  >
                    <span aria-hidden="true" className="flex shrink-0 text-fg-subtle">
                      {page.icon}
                    </span>
                    <span className="min-w-0 flex-1 truncate font-medium text-fg">
                      {page.label}
                    </span>
                    <span className="shrink-0 text-sm text-fg-muted">{page.where}</span>
                  </div>
                ))}
                <p className="px-2 pt-1 text-xs text-fg-subtle">{s.pinned.note}</p>
              </div>
            ) : null}
            {typed !== '' && pageHits.length > 0 ? (
              <div
                role="group"
                aria-labelledby={`${base}-g-pages`}
                data-testid="search-group-pages"
              >
                <div
                  id={`${base}-g-pages`}
                  className="flex items-baseline justify-between px-2 pb-1 text-xs font-semibold uppercase text-fg-subtle"
                >
                  <span>{navText.goToPages}</span>
                  <span className="font-normal normal-case">
                    {navText.pagesFound(pageHits.length)}
                  </span>
                </div>
                {pageHits.map((page, idx) => (
                  <div
                    key={page.id}
                    id={options[idx]?.id}
                    role="option"
                    aria-selected={idx === active}
                    tabIndex={-1}
                    data-testid="search-page-option"
                    data-href={page.href}
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onClick={() => {
                      choose(options[idx]);
                    }}
                    className={cn(optionClass(idx), 'flex items-center gap-2.5')}
                  >
                    <span aria-hidden="true" className="flex shrink-0 text-fg-subtle">
                      {page.icon}
                    </span>
                    <span className="min-w-0 flex-1 truncate font-medium text-fg">
                      {page.label}
                    </span>
                    <span className="shrink-0 text-sm text-fg-muted">{page.where}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {sections.map((v) =>
              v.items.length === 0 && !(v.hasMore && v.cursor !== null) ? null : (
                <div
                  key={v.sec.type}
                  role="group"
                  aria-labelledby={`${base}-g-${v.sec.type}`}
                  data-testid={`search-group-${v.sec.type}`}
                  data-partial={v.hasMore ? 'true' : 'false'}
                >
                  <div
                    id={`${base}-g-${v.sec.type}`}
                    className="flex items-baseline justify-between px-2 pb-1 text-xs font-semibold uppercase text-fg-subtle"
                  >
                    <span>{s.typeLabel[v.sec.type]}</span>
                    <span className="font-normal normal-case">
                      {s.sectionCount({ count: v.sec.count, partial: v.partial }, v.items.length)}
                    </span>
                  </div>
                  {v.items.length === 0 ? (
                    <p
                      className="px-2 pb-1 text-sm text-fg-muted"
                      data-testid={`search-partial-empty-${v.sec.type}`}
                    >
                      {s.partialEmpty}
                    </p>
                  ) : null}
                  {v.items.map((hit) => {
                    const idx = options.findIndex((o) => o.kind === 'hit' && o.hit.id === hit.id);
                    const o = options[idx];
                    return (
                      <div
                        key={hit.id}
                        id={o?.id}
                        role="option"
                        aria-selected={idx === active}
                        tabIndex={-1}
                        data-testid="search-option"
                        onMouseDown={(e) => {
                          e.preventDefault();
                        }}
                        onMouseEnter={() => {
                          if (wide) setActive(idx);
                        }}
                        onClick={() => {
                          choose(o);
                        }}
                        className={cn(optionClass(idx), 'flex items-start gap-2')}
                      >
                        <span className="min-w-0 flex-1">
                          <SearchHitView hit={hit} showType />
                        </span>
                        {!wide ? (
                          // Seçenek (role="option") içinde etkileşimli öğe olamaz (WCAG 4.1.2, axe nested-interactive): göz
                          // simgesi yalnız dokunma/işaretçi kısayoludur; klavye ve ekran okuyucu eşdeğeri seçili sonuçta sağ ok
                          // tuşudur (girişin açıklamasında söylenir)
                          <span
                            aria-hidden="true"
                            title={s.preview.show(hit.title)}
                            onMouseDown={(e) => {
                              e.preventDefault();
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewHit(hit);
                            }}
                            className="inline-flex min-h-control min-w-control shrink-0 items-center justify-center rounded text-fg-muted hover:text-fg"
                            data-testid="search-preview-button"
                          >
                            <Eye size={16} aria-hidden="true" />
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                  {v.hasMore && v.cursor !== null
                    ? (() => {
                        const idx = options.findIndex(
                          (o) => o.kind === 'more' && o.type === v.sec.type,
                        );
                        return (
                          <div
                            id={options[idx]?.id}
                            role="option"
                            aria-selected={idx === active}
                            aria-busy={v.loading}
                            tabIndex={-1}
                            data-testid={`search-continue-${v.sec.type}`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                            }}
                            onClick={() => {
                              choose(options[idx]);
                            }}
                            className={cn(
                              optionClass(idx),
                              'flex items-center gap-2 text-sm font-medium text-primary',
                            )}
                          >
                            {v.loading ? <Spinner size="sm" label={s.loading} /> : null}
                            <span>{s.continueSearchIn(s.typeLabel[v.sec.type])}</span>
                            {v.error !== null ? (
                              <span className="font-normal text-fg-muted">{s.error[v.error]}</span>
                            ) : null}
                          </div>
                        );
                      })()
                    : null}
                </div>
              ),
            )}
            {allIdx >= 0 ? (
              <div
                id={`${base}-o-all`}
                role="option"
                aria-selected={active === allIdx}
                tabIndex={-1}
                data-testid="search-see-all"
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={() => {
                  choose(options[allIdx]);
                }}
                className={cn(optionClass(allIdx), 'text-sm font-medium text-primary')}
              >
                {s.seeAll(debounced)}
              </div>
            ) : null}
          </div>
          {wide && typed !== '' ? (
            <div
              className="hidden border-l border-border pl-4 md:block"
              data-testid="search-preview-pane"
            >
              {shownHit !== null ? (
                <Suspense fallback={<Spinner />}>
                  <SearchPreview
                    key={shownHit.id}
                    hit={shownHit}
                    onOpen={() => {
                      go(recordHref(shownHit.type, shownHit.id));
                    }}
                  />
                </Suspense>
              ) : (
                <p className="text-sm text-fg-muted">{s.preview.hint}</p>
              )}
            </div>
          ) : null}
        </div>
        {typed === '' && pinned.length === 0 ? (
          <p className="text-sm text-fg-subtle" data-testid="search-pinned-empty">
            {s.pinned.empty}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
